import type { Currency } from '@ps/web-core-enums';
import classNames from 'clsx';
import React from 'react';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import Translation from '@swf/components/Translation/Translation';
import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import type { MyBetPart, ResultType } from '@swf/generated/types/api/sports';
import { PriceType } from '@swf/generated/types/api/sports';
import { Icon, Odds } from '@swf/sports/components';
import EachWayPlaceTerms from '@swf/sports/components/EachWayPlaceTerms/EachWayPlaceTerms';
import isOddsBoostMarket from '@swf/sports/utils/isOddsBoostMarket';

import BetDeduction from '../../../BetDeduction/BetDeduction';
import BetEventStatus from '../../../BetEventStatus/BetEventStatus';
import EventLink from '../EventLink/EventLink';
import EventTypeIcon from '../EventTypeIcon/EventTypeIcon';
import SelectionName from '../SelectionName/SelectionName';

import { TXTWCL_EW, TXTWCL_SP, TXTWCL_odds_boost } from './Part.messages.json';
import styles from './Part.scss';

export interface Props {
  part: MyBetPart;
  isSettled?: boolean;
  result?: ResultType;
  currency: Currency;
}

const Part: React.FC<Props> = ({ part, isSettled, result, currency }) => {
  const { formatMessage } = useRegulatoryIntl();

  const eventTypeId = Number(part.sportId);
  const hasEachWay = part.eachwayFactor && part.eachwayPlaces;
  const deadHeat = Boolean(part.deadHeatEachwayDeductions || part.deadHeatWinDeductions);
  const displayStartingPrice = !isSettled && part.priceType === PriceType.STARTING;

  return (
    <div className={styles.wrapper} key={part.eventMarketTemplateDescription}>
      <div className={styles.details}>
        <EventTypeIcon
          result={result}
          eventTypeId={eventTypeId}
          deadHeat={deadHeat}
          inPlay={part.inPlay}
        />
        <div>
          <div className={classNames(styles.eventDetails, result === 'VOID' && styles.voided)}>
            <span className={styles.selection}>
              <SelectionName part={part} />
              {(part.odds || hasEachWay || displayStartingPrice) && '@ '}
              {displayStartingPrice && <Translation {...TXTWCL_SP} />}
              {!displayStartingPrice && part.odds && <Odds {...part.odds} />}
              {hasEachWay && (
                <>
                  {` `}
                  <Translation {...TXTWCL_EW} />
                </>
              )}
              {isOddsBoostMarket(part.marketType) && (
                <Icon
                  className={styles.oddsboostIcon}
                  name="price-boost"
                  label={formatMessage(TXTWCL_odds_boost)}
                />
              )}
            </span>

            <EventLink part={part} isSettled={isSettled} />
            {hasEachWay && part.eachwayPlaces && (
              <EachWayPlaceTerms
                placeTerms={{
                  deductionFraction: `${part.eachwayFactor?.numerator}/${part.eachwayFactor?.denominator}`,
                  numPlaces: part.eachwayPlaces
                }}
              />
            )}

            <BetEventStatus part={part} isSettled={isSettled} />
          </div>

          {!!part.rule4Deductions && (
            <BetDeduction
              type="rule4"
              value={<CurrencyAmount value={part.rule4Deductions} currency={currency} />}
            />
          )}
          {!!part.deadHeatEachwayDeductions && <BetDeduction type="deadHeatEachway" />}
          {!!part.deadHeatWinDeductions && <BetDeduction type="deadHeatWin" />}
        </div>
      </div>
    </div>
  );
};

export default Part;
